/**
 * Fleet counts by contract generation: server `count()` aggregations over the admin-only
 * `walletKeyInventories`, `sessionKeyPermissions` (wallet-service 0.74.0) and
 * `walletUpgradeOffers` (0.75.0) projections. A timestamped snapshot refreshed by hand, like the
 * dashboard's platform totals — counts are never live listeners over the fleet.
 *
 * Every query is equality-only, single-field, or the per-generation exposed count on the 0.75.0
 * index `walletKeyInventories (contractGeneration, legacySignatureCapableKeyCount desc)`. Each
 * count succeeds or fails on its own: a missing rule or index shows as an unavailable cell, not
 * as a zero.
 */
import { collection, getCountFromServer, query, where } from "firebase/firestore";
import type { WhereFilterOp } from "firebase/firestore";
import { db } from "@/config/firebase";
import { COLLECTIONS } from "@/firestore/collections";
import type { CollectionName } from "@/firestore/collections";
import { REGISTERED_GENERATIONS } from "@/wallet/generations";
import { UPGRADE_OFFER_STATES } from "@/wallet/upgradeOffers";
import type { UpgradeOfferState } from "@/wallet/upgradeOffers";

/** Wallet states broken out per generation. */
export const FLEET_WALLET_STATES = ["provisioned", "migrating", "migrated", "retired"] as const;
export type FleetWalletState = (typeof FLEET_WALLET_STATES)[number];

export type CountFilter = [field: string, op: WhereFilterOp, value: unknown];

export interface CountSpec {
  id: string;
  collection: CollectionName;
  filters: CountFilter[];
}

const INVENTORIES = COLLECTIONS.walletKeyInventories;

/** Every count the fleet page takes, keyed by a stable id. */
export function generationCountPlan(): CountSpec[] {
  const plan: CountSpec[] = [];
  for (const { key } of REGISTERED_GENERATIONS) {
    plan.push({
      id: `gen:${key}`,
      collection: INVENTORIES,
      filters: [["contractGeneration", "==", key]],
    });
    plan.push({
      id: `gen:${key}:exposed`,
      collection: INVENTORIES,
      filters: [
        ["contractGeneration", "==", key],
        ["legacySignatureCapableKeyCount", ">", 0],
      ],
    });
    for (const state of FLEET_WALLET_STATES) {
      plan.push({
        id: `gen:${key}:${state}`,
        collection: INVENTORIES,
        filters: [
          ["contractGeneration", "==", key],
          ["walletState", "==", state],
        ],
      });
    }
  }
  // A summary a key event created before its wallet's first event (or not backfilled yet).
  plan.push({
    id: "gen:unknown",
    collection: INVENTORIES,
    filters: [["contractGeneration", "==", null]],
  });
  plan.push({
    id: "legacyWallets",
    collection: INVENTORIES,
    filters: [["legacySignatureCapableKeyCount", ">", 0]],
  });
  // Every exposed key fleet-wide, whatever its state, and the revoked ones among them.
  plan.push({
    id: "exposedKeys",
    collection: COLLECTIONS.sessionKeyPermissions,
    filters: [["legacySignatureCapable", "==", true]],
  });
  plan.push({
    id: "exposedKeysRevoked",
    collection: COLLECTIONS.sessionKeyPermissions,
    filters: [
      ["legacySignatureCapable", "==", true],
      ["state", "==", "revoked"],
    ],
  });
  for (const state of UPGRADE_OFFER_STATES) {
    plan.push({
      id: `offers:${state}`,
      collection: COLLECTIONS.walletUpgradeOffers,
      filters: [["state", "==", state]],
    });
  }
  plan.push({ id: "inventories", collection: INVENTORIES, filters: [] });
  // The backend's own backfill check: summaries carrying the wallet fields vs every wallet.
  plan.push({
    id: "inventoriesWithWallet",
    collection: INVENTORIES,
    filters: [["walletUpdatedAt", "!=", null]],
  });
  plan.push({ id: "wallets", collection: COLLECTIONS.wallets, filters: [] });
  return plan;
}

export type CountResult = { ok: true; value: number } | { ok: false; error: Error };

export interface GenerationCountRow {
  /** Null for the "unknown generation" row. */
  key: string | null;
  deprecated: boolean;
  total: CountResult;
  /** Wallets of the generation with an exposed (1271-capable) key; null on the unknown row. */
  exposed: CountResult | null;
  byState: Record<FleetWalletState, CountResult> | null;
}

export interface GenerationCounts {
  rows: GenerationCountRow[];
  legacyWallets: CountResult;
  /** `sessionKeyPermissions where legacySignatureCapable == true`, and the revoked ones. */
  exposedKeys: CountResult;
  exposedKeysRevoked: CountResult;
  /** Upgrade offers per state (wallet-service 0.75.0). */
  offers: Record<UpgradeOfferState, CountResult>;
  inventories: CountResult;
  inventoriesWithWallet: CountResult;
  wallets: CountResult;
  fetchedAt: number;
  /** Set when every key-inventory count failed (collections not readable here): the first error. */
  unavailable: Error | null;
}

const MISSING: CountResult = { ok: false, error: new Error("Not counted.") };

export function assembleGenerationCounts(
  results: ReadonlyMap<string, CountResult>,
  fetchedAt: number,
): GenerationCounts {
  const get = (id: string) => results.get(id) ?? MISSING;
  const rows: GenerationCountRow[] = REGISTERED_GENERATIONS.map(({ key, deprecated }) => ({
    key,
    deprecated,
    total: get(`gen:${key}`),
    exposed: get(`gen:${key}:exposed`),
    byState: Object.fromEntries(
      FLEET_WALLET_STATES.map((state) => [state, get(`gen:${key}:${state}`)]),
    ) as Record<FleetWalletState, CountResult>,
  }));
  rows.push({
    key: null,
    deprecated: false,
    total: get("gen:unknown"),
    exposed: null,
    byState: null,
  });
  // The key inventory's own counts: the wallets count and the offers (a later release) aside.
  const inventoryResults = [...results.entries()]
    .filter(([id]) => id !== "wallets" && !id.startsWith("offers:"))
    .map(([, r]) => r);
  const firstError = inventoryResults.find((r): r is { ok: false; error: Error } => !r.ok);
  return {
    rows,
    legacyWallets: get("legacyWallets"),
    exposedKeys: get("exposedKeys"),
    exposedKeysRevoked: get("exposedKeysRevoked"),
    offers: Object.fromEntries(
      UPGRADE_OFFER_STATES.map((state) => [state, get(`offers:${state}`)]),
    ) as Record<UpgradeOfferState, CountResult>,
    inventories: get("inventories"),
    inventoriesWithWallet: get("inventoriesWithWallet"),
    wallets: get("wallets"),
    fetchedAt,
    unavailable:
      inventoryResults.length > 0 && inventoryResults.every((r) => !r.ok) && firstError
        ? firstError.error
        : null,
  };
}

export type CountFn = (spec: CountSpec) => Promise<number>;

async function firestoreCount(spec: CountSpec): Promise<number> {
  const ref = collection(db, spec.collection);
  const q = spec.filters.length
    ? query(ref, ...spec.filters.map(([field, op, value]) => where(field, op, value)))
    : ref;
  return (await getCountFromServer(q)).data().count;
}

export async function fetchGenerationCounts(
  count: CountFn = firestoreCount,
  now: () => number = Date.now,
): Promise<GenerationCounts> {
  const plan = generationCountPlan();
  const settled = await Promise.allSettled(plan.map((spec) => count(spec)));
  const results = new Map<string, CountResult>();
  plan.forEach((spec, i) => {
    const outcome = settled[i];
    results.set(
      spec.id,
      outcome.status === "fulfilled"
        ? { ok: true, value: outcome.value }
        : {
            ok: false,
            error:
              outcome.reason instanceof Error ? outcome.reason : new Error(String(outcome.reason)),
          },
    );
  });
  return assembleGenerationCounts(results, now());
}
